/**
 * hooks/useUploadReminder.js
 *
 * Computes a compact "upload reminder" state for the dashboard tile + the
 * transactions banner. Pure client-side; the snapshot is already fetched
 * elsewhere in the shell, so this is free.
 *
 * Fires when ANY of these conditions holds:
 *   • stale       — no transaction recorded in the last 14 days
 *   • month_end   — today is within the LAST 5 days of the current month
 *   • new_month   — today is within the FIRST 3 days of a new month
 *
 * Multiple conditions can hold at once; we pick the most actionable one
 * (month_end → new_month → stale) and expose it as `reason`.
 *
 * Copy is deliberately calm and non-nagging:
 *   • month_end → "Your monthly recap lands on the 1st — upload before then
 *                so it reflects the full month."
 *   • new_month → "Last chance to catch [month]'s numbers — upload now and
 *                the picture stays honest."
 *   • stale     → "It's been {N} days since your last recorded transaction.
 *                A quick refresh keeps the Sunday brief useful."
 *
 * Never fires when the user has zero transactions total — that's the
 * EMPTY-state onboarding surface's job, not ours.
 */
import { useMemo } from 'react';

const STALE_THRESHOLD_DAYS = 14;
const MONTH_END_TAIL_DAYS  = 5;   // last N days of the current month
const NEW_MONTH_HEAD_DAYS  = 3;   // first N days of the new month

function daysBetween(iso, today = new Date()) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const ms = today.getTime() - d.getTime();
  return Math.floor(ms / (24 * 3600 * 1000));
}

function currentMonthLabel(today = new Date()) {
  return today.toLocaleDateString(undefined, { month: 'long' });
}
function prevMonthLabel(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long' });
}

function daysUntilMonthEnd(today = new Date()) {
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0); // last day of month
  return Math.floor((last.getTime() - today.getTime()) / (24 * 3600 * 1000));
}
function dayOfMonth(today = new Date()) {
  return today.getDate();
}

export function useUploadReminder(snapshot) {
  return useMemo(() => {
    if (!snapshot) return null;
    const flags = snapshot.flags || {};
    // Skip empty-state users — those go through the onboarding EMPTY tile.
    if (flags.has_transactions === false) return null;

    const lastTxnDate = snapshot?.cashflow?.last_txn_date || null;
    const staleDays = daysBetween(lastTxnDate);

    const today = new Date();
    const dEnd  = daysUntilMonthEnd(today);
    const dom   = dayOfMonth(today);

    const isMonthEnd = dEnd <= (MONTH_END_TAIL_DAYS - 1);
    const isNewMonth = dom <= NEW_MONTH_HEAD_DAYS;
    const isStale    = staleDays != null && staleDays >= STALE_THRESHOLD_DAYS;

    if (!isMonthEnd && !isNewMonth && !isStale) return null;

    // Priority: month_end > new_month > stale (most actionable first).
    if (isMonthEnd) {
      return {
        reason:      'month_end',
        title:       'Your monthly recap is around the corner',
        message:     `${currentMonthLabel()} closes in ${dEnd + 1} day${dEnd + 1 === 1 ? '' : 's'}. Upload your latest statement so the recap reflects the whole month.`,
        cta:         'Upload / add transactions',
        cta_href:    '/transactions?add=1',
        stale_days:  staleDays,
      };
    }
    if (isNewMonth) {
      return {
        reason:      'new_month',
        title:       `Catch ${prevMonthLabel()} before it settles`,
        message:     `Upload your latest statement now and your recap picture stays honest.`,
        cta:         'Upload / add transactions',
        cta_href:    '/transactions?add=1',
        stale_days:  staleDays,
      };
    }
    return {
      reason:      'stale',
      title:       'Your data has gone quiet',
      message:     staleDays != null
        ? `It’s been ${staleDays} day${staleDays === 1 ? '' : 's'} since your last recorded transaction. A quick refresh keeps the Sunday brief useful.`
        : 'Add a few recent transactions to keep the Sunday brief useful.',
      cta:         'Refresh in 2120',
      cta_href:    '/transactions?add=1',
      stale_days:  staleDays,
    };
  }, [snapshot]);
}

export default useUploadReminder;
